// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Credential } from '@daml.js/dtc-credential/lib/Dtc/Credential/V3/Credential';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { useParticipantRemoveWalletsFromAllowList } from '../../../hooks/mutations/registry/allowList/useParticipantRemoveWalletsFromAllowList';
import { useRegistrarRemoveWalletsFromAllowList } from '../../../hooks/mutations/registry/allowList/useRegistrarRemoveWalletsFromAllowList';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import useAllowListRole, {
  AllowListViewMode,
} from '../../../hooks/queries/registry/allowList/useAllowListRole';
import useAllowLists from '../../../hooks/queries/registry/allowList/useAllowLists';
import TemplateContract from '../../../utils/TemplateContract';
import { decodeAllowListName } from '../../../utils/urls';
import { PageWrapper } from '../../PageWrapper';
import { AddWalletDialog } from './AddWalletDialog';
import { isMemberOfAllowList } from './utils';

/**
 * Represents a wallet (holder/party) on an allow list with their associated credentials.
 */
interface AllowListWallet {
  holder: string;
  credentials: TemplateContract<Credential>[];
}

type AllowListDetailPageProps = {
  mode?: AllowListViewMode;
};

export const AllowListDetailPage = ({ mode: initialMode }: AllowListDetailPageProps) => {
  const navigate = useNavigate();
  const { name: encodedName } = useParams<{ name: string }>();

  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());

  const { mode: detectedMode } = useAllowListRole();
  const allowLists = useAllowLists();
  const credentials = useCredentials();
  const registrarRemoveWallets = useRegistrarRemoveWalletsFromAllowList();
  const participantRemoveWallets = useParticipantRemoveWalletsFromAllowList();

  const mode = initialMode ?? detectedMode;
  const isRegistrarView = mode === 'registrar';
  const removeWallets = isRegistrarView ? registrarRemoveWallets : participantRemoveWallets;
  const listPageName = isRegistrarView ? 'Master Allow List' : 'Allow Lists';

  // Decode the allow list name from URL and find the matching allow list
  const allowListName = encodedName ? decodeAllowListName(encodedName) : undefined;
  const allowList = allowLists.data?.find((al) => al.payload.name === allowListName);

  // Get wallets of this allow list - grouped by holder (party)
  const wallets: AllowListWallet[] = useMemo(() => {
    if (!credentials.data || !allowList) return [];

    // Filter credentials that have the membership claim for this allow list
    const walletCredentials = credentials.data.filter((cred) =>
      isMemberOfAllowList(cred, allowList.payload.name),
    );

    // Group by holder
    const holderMap = new Map<string, TemplateContract<Credential>[]>();
    walletCredentials.forEach((cred) => {
      const { holder } = cred.payload;
      const existing = holderMap.get(holder) ?? [];
      holderMap.set(holder, [...existing, cred]);
    });

    // Convert to array
    return Array.from(holderMap.entries()).map(([holder, creds]) => ({
      holder,
      credentials: creds,
    }));
  }, [credentials.data, allowList]);

  const isLoading = allowLists.isLoading || credentials.isLoading;

  if (isLoading) return <Loading />;

  if (!allowList) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Allow List not found</Typography>
        <Button onClick={() => navigate('/registry/allow-lists')} startIcon={<ArrowBack />}>
          Back to {listPageName}
        </Button>
      </Box>
    );
  }

  const handleToggleWallet = (holder: string) => {
    setSelectedWallets((prev) => {
      const next = new Set(prev);
      if (next.has(holder)) {
        next.delete(holder);
      } else {
        next.add(holder);
      }
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    if (selectedWallets.size === 0) return;

    // Get all credential IDs for the selected wallets
    const credentialCids = wallets
      .filter((m) => selectedWallets.has(m.holder))
      .flatMap((m) => m.credentials.map((c) => c.contractId));

    await removeWallets.mutateAsync({
      allowListCid: allowList.contractId,
      credentialCids,
    });
    setSelectedWallets(new Set());
  };

  const handleRemoveWallet = async (wallet: AllowListWallet) => {
    await removeWallets.mutateAsync({
      allowListCid: allowList.contractId,
      credentialCids: wallet.credentials.map((c) => c.contractId),
    });

    // Clear this wallet from selection if selected (after successful removal)
    setSelectedWallets((prev) => {
      if (prev.has(wallet.holder)) {
        const next = new Set(prev);
        next.delete(wallet.holder);
        return next;
      }
      return prev;
    });
  };

  const buttons = [
    {
      name: 'Add Wallet',
      onClick: () => setDialogName('add-wallets'),
    },
    {
      name: 'Remove Selected',
      onClick: handleRemoveSelected,
      disabled: selectedWallets.size === 0,
    },
  ];

  const pageTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
      <IconButton onClick={() => navigate('/registry/allow-lists')} sx={{ mr: 1 }}>
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {isRegistrarView ? 'Master Allow List' : 'Allow List'}
        </Typography>
        <Typography fontWeight="bold" color="primary" variant="h4">
          {allowList.payload.name}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <PageWrapper buttons={buttons} pageName={pageTitle}>
      {dialogName === 'add-wallets' && (
        <AddWalletDialog
          open
          onClose={onCloseDialog}
          allowList={allowList}
          mode={isRegistrarView ? 'registrar' : 'participant'}
        />
      )}

      {/* Wallets Section */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Wallets ({wallets.length})
        </Typography>
        {wallets.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {'No wallets in this allow list. Add a wallet using the "Add Wallet" button.'}
          </Typography>
        ) : (
          <List dense>
            {wallets.map((wallet) => (
              <ListItem
                key={wallet.holder}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="remove"
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={() => handleRemoveWallet(wallet)}
                  >
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemButton
                  onClick={() => {
                    handleToggleWallet(wallet.holder);
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedWallets.has(wallet.holder)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemText
                    primary={<PartyDisplay party={wallet.holder} />}
                    secondary={`${wallet.credentials.length} credential${wallet.credentials.length !== 1 ? 's' : ''}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </PageWrapper>
  );
};
